import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import {
  buildOrganisationJsonLd,
  serialiseJsonLd
} from '@lib/page-utils/structured-data';
import { toCanonicalPath } from '@lib/utils';

type ShareImage = { path: string; width: number; height: number };

export type PageSeo = {
  title: string;
  description: string;
  shareImage: ShareImage;
};

const SHARE_IMAGE_PATH_PREFIX = '/images/share';

const HUB_SHARE_IMAGE: ShareImage = {
  path: `${SHARE_IMAGE_PATH_PREFIX}/hub.jpg`,
  width: 1024,
  height: 576
};

const toolShareImage = (name: string): ShareImage => ({
  path: `${SHARE_IMAGE_PATH_PREFIX}/${name}.jpg`,
  width: 1200,
  height: 628
});

/**
 * Head copy for the tool and landing pages that took it from Yoast until
 * FFH-2632. Copied verbatim from what each page served then, so the move off
 * WordPress changes nothing a search engine sees. The share images were
 * copied from the WordPress media library into public/images/share.
 */
export const PAGE_SEO = {
  home: {
    title: 'Fantasy Football Hub: Fantasy Premier League Tips for 2026/27',
    description:
      'Winning at Fantasy Premier League just got easier. Dominate your mini-league today with our FPL tips, tools, and resources.',
    shareImage: HUB_SHARE_IMAGE
  },
  join: {
    title: 'Join the Hub - Fantasy Football Hub',
    description:
      "Join the Hub today with our guarantee that you'll win your FPL mini-league - or your money back! Get instant access to Hub AI, expert team reveals and more.",
    shareImage: HUB_SHARE_IMAGE
  },
  playerProfile: {
    title: 'FPL player profiles | Fantasy Football Hub',
    description:
      'Up to date stats, graphics, upcoming fixtures, heatmaps and predicted points for every Premier League player.',
    shareImage: toolShareImage('player-profile')
  },
  matchCentre: {
    title: 'Fantasy Football Hub FPL Match Centre',
    description:
      'Keep up to date with the live FPL action from every Premier League game, including goals, assists, cards, bonus and effective ownership.',
    shareImage: toolShareImage('match-centre')
  },
  fixtureTicker: {
    title: 'FPL Fixture Difficulty Research Tool - Fantasy Football Hub',
    description:
      'Our FPL Fixture Difficulty Tool identifies the teams with the best Fantasy Premier League fixtures to help you win your mini-league.',
    shareImage: toolShareImage('fixture-ticker')
  },
  aiTeams: {
    title: 'My Team FPL: Best AI Wildcard and Free Hit teams',
    description:
      'The best FPL Wildcard and FPL Free Hit teams for the current gameweek according to Fantasy Football Hub’s My Team AI.',
    shareImage: toolShareImage('ai-teams')
  },
  transfers: {
    title: 'My Team FPL: The best FPL transfers for your team based on AI',
    description:
      'Personalised recommended transfers for your FPL team by Fantasy Football Hub’s My Team artificial intelligence tool.',
    shareImage: toolShareImage('transfers')
  },
  pickTeam: {
    title: 'My Team FPL: Rate my team tool and predicted points',
    description:
      'Get your FPL team rated, see its predicted points according to AI and make plans for future gameweeks with the My Team tool.',
    shareImage: {
      path: `${SHARE_IMAGE_PATH_PREFIX}/my-team.jpg`,
      width: 720,
      height: 405
    }
  },
  priceRises: {
    title: 'FPL player price change prediction tool | Fantasy Football Hub',
    description:
      "Want to know which FPL players will rise and fall in price each day? Fantasy Football Hub's player price changes prediction tool can help.",
    shareImage: {
      path: `${SHARE_IMAGE_PATH_PREFIX}/price-rises.jpg`,
      width: 720,
      height: 377
    }
  },
  playerComparison: {
    title:
      'Fantasy Premier League Player Comparison Tool - Fantasy Football Hub',
    description:
      'Fantasy Premier League player and team comparison tool. Compare sets of FPL players on all the key metrics and stats.',
    shareImage: toolShareImage('player-comparison')
  },
  opta: {
    title: 'FPL Stats by OPTA - Player and Team Stats - Fantasy Football Hub',
    description:
      'Our OPTA tool for FPL is the best place to find the latest Fantasy Premier League stats, updated within minutes after every gameweek.',
    shareImage: {
      path: `${SHARE_IMAGE_PATH_PREFIX}/opta.jpg`,
      width: 720,
      height: 377
    }
  },
  predictions: {
    title: 'FPL Predicted Points Tool - Fantasy Football Hub',
    description:
      'FPL Predicted Points tool which will help you win at fantasy football through artificial intelligence.',
    shareImage: toolShareImage('predictions')
  },
  ultraHowItWorks: {
    title:
      'Ultra - The most advanced FPL advice service on the planet - Fantasy Football Hub',
    description:
      'Our highest membership tier, Ultra offer Hub members an opportunity to be coached by some of the top players in fantasy football. Like many other sports and games, if you want to get better you can be taught by those more experienced.',
    shareImage: {
      path: `${SHARE_IMAGE_PATH_PREFIX}/ultra.jpg`,
      width: 1024,
      height: 535
    }
  }
} satisfies Record<string, PageSeo>;

/**
 * Page-level head tags that win over `_app`'s defaults (see lib/seo.tsx for
 * the next/head de-duplication rules the keys rely on). Without copy, the page
 * keeps the defaults and gets only its canonical, as it did when Yoast had
 * nothing for it.
 */
export const getPageSeoHead = (pathname: string, seo?: PageSeo) => {
  const canonicalUrl = `${FFH_HOSTNAME}${toCanonicalPath(pathname)}`;
  const canonical = (
    <link rel="canonical" href={canonicalUrl} key="canonical" />
  );
  if (!seo) return [canonical];

  const shareImageUrl = `${FFH_HOSTNAME}${seo.shareImage.path}`;
  return [
    canonical,
    <title key="title">{seo.title}</title>,
    <meta name="description" content={seo.description} key="description" />,
    <meta property="og:title" content={seo.title} key="og:title" />,
    <meta
      property="og:description"
      content={seo.description}
      key="og:description"
    />,
    <meta property="og:url" content={canonicalUrl} key="og:url" />,
    <meta property="og:image" content={shareImageUrl} key="og:image" />,
    <meta
      property="og:image:width"
      content={String(seo.shareImage.width)}
      key="og:image:width"
    />,
    <meta
      property="og:image:height"
      content={String(seo.shareImage.height)}
      key="og:image:height"
    />,
    <meta
      name="twitter:card"
      content="summary_large_image"
      key="twitter:card"
    />
  ];
};

const WEBSITE_ID = `${FFH_HOSTNAME}/#website`;

/**
 * The site identity Yoast used to print on the homepage. Its sitelinks
 * SearchAction is left out: Google stopped showing the search box in 2024.
 */
export const buildHomepageJsonLd = (): string =>
  serialiseJsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: `${FFH_HOSTNAME}/`,
        name: SITE_NAME
      },
      buildOrganisationJsonLd()
    ]
  });
